import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  MoonIcon,
  GlobeAltIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  BugAntIcon,
  ShoppingCartIcon,
  KeyIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  DocumentIcon,
  GiftIcon,
  BoltIcon,
  CakeIcon,
  PlusIcon,
  HeartIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { useFamilyStore } from '../../store/familyStore';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import { requestNotificationPermission, getNotificationStatus } from '../../push/initNotifications';
import { registerPushSubscription } from '../../push/registerPush';
import Cropper, { Area } from 'react-easy-crop';
import { signInWithGoogle } from '../../services/googleAuth';

interface ProfileForm {
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
}

interface SpecialDate {
  id: string;
  date_type: string;
  date: string;
  name: string | null;
  is_recurring: boolean;
}

// Ring icon for anniversaries
const RingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v4" />
    <path d="M9 3l3 3 3-3" />
  </svg>
);

const dateTypeLabels: Record<string, { label: string; icon: React.ComponentType<any>; color: string; emoji: string }> = {
  birthday: { label: 'Birthday', icon: CakeIcon, color: 'text-pink-400', emoji: '🎂' },
  nameday: { label: 'Name Day', icon: CalendarIcon, color: 'text-purple-400', emoji: '📅' },
  anniversary: { label: 'Anniversary', icon: RingIcon, color: 'text-amber-400', emoji: '💍' },
  wedding: { label: 'Wedding Anniversary', icon: RingIcon, color: 'text-amber-400', emoji: '💒' },
  other: { label: 'Other', icon: SparklesIcon, color: 'text-blue-400', emoji: '✨' },
};


export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [notifications, setNotifications] = useState(true);
  const [notifyOwnActions, setNotifyOwnActions] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    shopping: true,
    passwords: true,
    todos: true,
    calendar: true,
    documents: true,
    gifts: true,
    activities: true,
  });
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processingAvatar, setProcessingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [clearingActivities, setClearingActivities] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [reregistering, setReregistering] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);
  const [showLinkPasswordModal, setShowLinkPasswordModal] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [pendingGoogleToken, setPendingGoogleToken] = useState<string | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [specialDates, setSpecialDates] = useState<SpecialDate[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [showAddDateModal, setShowAddDateModal] = useState(false);
  const [newDateType, setNewDateType] = useState('birthday');
  const [newDate, setNewDate] = useState('');
  const [newDateName, setNewDateName] = useState('');
  const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  const resolvedAvatarUrl = user?.avatar_url
    ? (user.avatar_url.startsWith('http') ? user.avatar_url : `${apiBase}${user.avatar_url}`)
    : null;
  
  // Check if Google account is linked
  const hasGoogleAccount = user?.google_id !== null && user?.google_id !== undefined;
  
  // Debug: Log Google OAuth configuration
  useEffect(() => {
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    console.log('Google OAuth Config:', {
      hasClientId: !!googleClientId,
      clientIdLength: googleClientId?.length || 0,
      hasGoogleAccount,
      userGoogleId: user?.google_id,
    });
  }, [hasGoogleAccount, user?.google_id]);

  // Check PWA install status
  useEffect(() => {
    const checkInstallStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInApp = (window.navigator as any).standalone === true;
      const isInstalledCheck = isStandalone || isInApp;
      setIsInstalled(isInstalledCheck);
      if (isInstalledCheck) {
        setDeferredPrompt(null);
      }
    };

    checkInstallStatus();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (!isInstalled) {
        setDeferredPrompt(e);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    const interval = setInterval(checkInstallStatus, 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearInterval(interval);
    };
  }, [isInstalled]);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) {
      toast.error('Install prompt not available. Please use your browser\'s install option.');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setDeferredPrompt(null);
        toast.success('App installation started!');
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
      toast.error('Failed to show install prompt');
    }
  };
  const { register: registerProfile, handleSubmit: handleSubmitProfile, reset: resetProfile } = useForm<ProfileForm>({
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      date_of_birth: user?.date_of_birth ? user.date_of_birth.split('T')[0] : '',
    },
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLinkGoogle = async () => {
    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
      toast.error('Google OAuth is not configured');
      return;
    }

    try {
      setLinkingGoogle(true);
      const idToken = await signInWithGoogle();
      
      // Store token and show password modal
      setPendingGoogleToken(idToken);
      setShowLinkPasswordModal(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to authenticate with Google');
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleConfirmLinkGoogle = async () => {
    if (!pendingGoogleToken || !linkPassword) {
      toast.error('Password is required');
      return;
    }

    try {
      setLinkingGoogle(true);
      const response = await api.post('/auth/link-google', {
        id_token: pendingGoogleToken,
        password: linkPassword,
      });
      
      // Update user in store
      useAuthStore.getState().setUser(response.data);
      
      toast.success('Google account linked successfully');
      setShowLinkPasswordModal(false);
      setLinkPassword('');
      setPendingGoogleToken(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link Google account');
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (!confirm('Are you sure you want to unlink your Google account? You will need to use your password to log in.')) {
      return;
    }

    try {
      setUnlinkingGoogle(true);
      await useAuthStore.getState().unlinkGoogleAccount();
      
      toast.success('Google account unlinked successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unlink Google account');
    } finally {
      setUnlinkingGoogle(false);
    }
  };

  const handleToggleNotifications = async () => {
    const newValue = !notifications;
    
    // If enabling, request browser permission and register push
    if (newValue) {
      const status = getNotificationStatus();
      if (!status.supported) {
        toast.error('Notifications are not supported in this browser');
        return;
      }
      
      const success = await requestNotificationPermission();
      if (!success) {
        toast.error(t('settings.notificationsDescOff'));
        return;
      }
    }
    
    // Update server-side preference
    try {
      await api.post(`/notifications/toggle?enabled=${newValue}`);
      setNotifications(newValue);
      toast.success(newValue ? t('settings.notificationsDescOn') : 'Notifications disabled');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to update notification settings');
    }
  };

  const handleThemeToggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    if (next === 'light') {
      document.documentElement.classList.add('theme-light');
    } else {
      document.documentElement.classList.remove('theme-light');
    }
    toast.success(next === 'light' ? t('settings.themeLight') : t('settings.themeDark'));
  };

  // Load notification status and preferences on mount
  useEffect(() => {
    const loadNotificationStatus = async () => {
      try {
        const res = await api.get('/notifications/preferences');
        setNotifications(res.data.notifications_enabled);
        setNotifyOwnActions(res.data.notify_own_actions || false);
        if (res.data.preferences) {
          setNotificationPreferences(res.data.preferences);
        }
      } catch {
        // ignore - default to true
      }
    };
    loadNotificationStatus();
  }, []);

  // Load user's special dates
  const loadSpecialDates = async () => {
    setLoadingDates(true);
    try {
      const res = await api.get('/gifts/special-dates/me');
      setSpecialDates(res.data);
    } catch (e) {
      console.error('Failed to load special dates:', e);
    } finally {
      setLoadingDates(false);
    }
  };

  useEffect(() => {
    loadSpecialDates();
  }, []);

  const handleAddSpecialDate = async () => {
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      await api.post('/gifts/special-dates', {
        date_type: newDateType,
        date: new Date(newDate).toISOString(),
        name: newDateName || null,
        is_recurring: true,
      });
      toast.success('Special date added!');
      setShowAddDateModal(false);
      setNewDate('');
      setNewDateName('');
      setNewDateType('birthday');
      loadSpecialDates();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to add special date');
    }
  };

  const handleDeleteSpecialDate = async (dateId: string) => {
    if (!confirm('Delete this special date?')) return;
    try {
      await api.delete(`/gifts/special-dates/${dateId}`);
      toast.success('Special date deleted');
      loadSpecialDates();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to delete special date');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  };

  const handleToggleFeatureNotification = async (feature: string) => {
    const newPreferences = {
      ...notificationPreferences,
      [feature]: !notificationPreferences[feature as keyof typeof notificationPreferences],
    };
    
    try {
      await api.put('/notifications/preferences', newPreferences);
      setNotificationPreferences(newPreferences);
      toast.success(`Notifications for ${feature} ${newPreferences[feature as keyof typeof notificationPreferences] ? 'enabled' : 'disabled'}`);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to update notification preferences');
    }
  };

  const handleToggleNotifyOwnActions = async () => {
    const newValue = !notifyOwnActions;
    
    try {
      await api.put(`/notifications/notify-own-actions?enabled=${newValue}`);
      setNotifyOwnActions(newValue);
      toast.success(newValue ? 'You will now receive notifications for your own actions' : 'You will no longer receive notifications for your own actions');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to update notification preference');
    }
  };

  const clearActivityLog = async () => {
    if (!confirm('Are you sure you want to clear all activity logs? This cannot be undone.')) return;
    setClearingActivities(true);
    try {
      const res = await api.delete('/activities/clear');
      toast.success(`Cleared ${res.data.deleted} activity entries`);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to clear activities');
    } finally {
      setClearingActivities(false);
    }
  };

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await api.get('/notifications/subscriptions');
      setDevices(res.data);
      
      // Get current device's subscription endpoint
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setCurrentEndpoint(subscription?.endpoint || null);
        }
      } catch (e) {
        console.warn('Could not get current subscription endpoint:', e);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleOpenDebugModal = () => {
    setShowDebugModal(true);
    loadDevices();
  };

  const handleReregister = async () => {
    setReregistering(true);
    try {
      const success = await registerPushSubscription(navigator.userAgent, true);
      if (success) {
        toast.success('Device re-registered successfully');
        await loadDevices();
      } else {
        toast.error('Failed to re-register device. Check console for details.');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to re-register device');
    } finally {
      setReregistering(false);
    }
  };

  const handleUnsubscribe = async (deviceId: string) => {
    if (!confirm('Are you sure you want to unsubscribe this device?')) return;
    try {
      await api.delete(`/notifications/subscriptions/${deviceId}`);
      toast.success('Device unsubscribed');
      await loadDevices();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to unsubscribe device');
    }
  };

  const onSaveProfile = async (data: ProfileForm) => {
    try {
      await api.patch('/users/me', {
        first_name: data.first_name,
        last_name: data.last_name || null,
        email: data.email || null,
        date_of_birth: data.date_of_birth ? new Date(data.date_of_birth).toISOString() : null,
      });
      // Refresh user info
      const me = await api.get('/users/me');
      useAuthStore.getState().setUser(me.data);
      toast.success(t('settings.editProfileDesc'));
      setShowProfileModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) throw new Error('No 2d context');

      const size = Math.max(pixelCrop.width, pixelCrop.height);
      canvas.width = size;
      canvas.height = size;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, size, size);

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        size,
        size
      );

      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas is empty'));
        }, 'image/jpeg', 0.92);
      });
    },
    []
  );

  const onUploadAvatar = async () => {
    if (!avatarPreview || !croppedAreaPixels) return;
    setAvatarUploading(true);
    setProcessingAvatar(true);
    try {
      const blob = await getCroppedImg(avatarPreview, croppedAreaPixels);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

      const form = new FormData();
      form.append('file', file);
      await api.post('/users/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const me = await api.get('/users/me');
      useAuthStore.getState().setUser(me.data);
      // Refresh family data to update avatar in family member list
      const { currentFamily, fetchFamily } = useFamilyStore.getState();
      if (currentFamily?.id) {
        fetchFamily(currentFamily.id);
      }
      toast.success(t('settings.editProfileDesc'));
      setShowAvatarCropper(false);
      setAvatarPreview(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
      setProcessingAvatar(false);
    }
  };

  const settingsGroups = [
    {
      title: t('settings.account'),
      items: [
        {
          icon: UserIcon,
          label: t('settings.editProfile'),
          description: t('settings.editProfileDesc'),
          action: () => {
            resetProfile({
              first_name: user?.first_name || '',
              last_name: user?.last_name || '',
              email: user?.email || '',
              date_of_birth: user?.date_of_birth ? user.date_of_birth.split('T')[0] : '',
            });
            setShowProfileModal(true);
          },
        },
        {
          icon: ShieldCheckIcon,
          label: t('settings.changePassword'),
          description: t('settings.changePasswordDesc'),
          action: () => toast('Coming soon!'),
        },
        ...(import.meta.env.VITE_GOOGLE_CLIENT_ID ? [{
          icon: GlobeAltIcon,
          label: hasGoogleAccount ? 'Unlink Google Account' : 'Link Google Account',
          description: hasGoogleAccount 
            ? 'Remove Google sign-in from your account' 
            : 'Enable Google sign-in for your account',
          action: hasGoogleAccount ? handleUnlinkGoogle : handleLinkGoogle,
        }] : []),
      ],
    },
    {
      title: t('settings.preferences'),
      items: [
        {
          icon: BellIcon,
          label: t('settings.notifications'),
          description: notifications ? t('settings.notificationsDescOn') : t('settings.notificationsDescOff'),
          action: handleToggleNotifications,
          toggle: true,
          enabled: notifications,
        },
        ...(notifications ? [
          {
            icon: BoltIcon,
            label: 'Receive notifications for my own actions',
            description: notifyOwnActions ? 'You will receive notifications when you perform actions' : 'You will not receive notifications for your own actions',
            action: handleToggleNotifyOwnActions,
            toggle: true,
            enabled: notifyOwnActions,
          },
          {
            icon: ShoppingCartIcon,
            label: 'Shopping Notifications',
            description: 'Get notified about shopping list updates',
            action: () => handleToggleFeatureNotification('shopping'),
            toggle: true,
            enabled: notificationPreferences.shopping,
          },
          {
            icon: KeyIcon,
            label: 'Password Notifications',
            description: 'Get notified about password changes',
            action: () => handleToggleFeatureNotification('passwords'),
            toggle: true,
            enabled: notificationPreferences.passwords,
          },
          {
            icon: ClipboardDocumentListIcon,
            label: 'Todo Notifications',
            description: 'Get notified about todo updates and reminders',
            action: () => handleToggleFeatureNotification('todos'),
            toggle: true,
            enabled: notificationPreferences.todos,
          },
          {
            icon: CalendarIcon,
            label: 'Calendar Notifications',
            description: 'Get notified about calendar events',
            action: () => handleToggleFeatureNotification('calendar'),
            toggle: true,
            enabled: notificationPreferences.calendar,
          },
          {
            icon: DocumentIcon,
            label: 'Document Notifications',
            description: 'Get notified about document updates',
            action: () => handleToggleFeatureNotification('documents'),
            toggle: true,
            enabled: notificationPreferences.documents,
          },
          {
            icon: GiftIcon,
            label: 'Gift Notifications',
            description: 'Get notified about gift list updates',
            action: () => handleToggleFeatureNotification('gifts'),
            toggle: true,
            enabled: notificationPreferences.gifts,
          },
          {
            icon: BoltIcon,
            label: 'Activity Notifications',
            description: 'Get notified about general family activities',
            action: () => handleToggleFeatureNotification('activities'),
            toggle: true,
            enabled: notificationPreferences.activities,
          },
        ] : []),
        {
          icon: MoonIcon,
          label: t('settings.theme'),
          description: theme === 'light' ? t('settings.themeLight') : t('settings.themeDark'),
          action: handleThemeToggle,
        },
        {
          icon: GlobeAltIcon,
          label: t('settings.language'),
          description: t(`settings.languages.${language as 'en' | 'pl' | 'uk'}`),
          action: () => {},
          custom: (
            <select
              className="input w-32 bg-surface-800"
              value={language}
              onChange={(e) => {
                const lng = e.target.value;
                setLanguage(lng);
                i18n.changeLanguage(lng);
                localStorage.setItem('i18n-lang', lng);
                toast.success(t('settings.language'));
              }}
            >
              <option value="en">{t('settings.languages.en')}</option>
              <option value="pl">{t('settings.languages.pl')}</option>
              <option value="uk">{t('settings.languages.uk')}</option>
            </select>
          ),
        },
      ],
    },
    {
      title: t('settings.devices'),
      items: [
        ...(!isInstalled ? [{
          icon: DevicePhoneMobileIcon,
          label: 'Install the app',
          description: deferredPrompt ? 'Install this app on your device' : 'Install prompt will be available soon',
          action: handleInstallPWA,
          disabled: !deferredPrompt,
        }] : []),
        {
          icon: DevicePhoneMobileIcon,
          label: t('settings.manageDevices'),
          description: t('settings.manageDevicesDesc'),
          action: handleOpenDebugModal,
        },
      ],
    },
    {
      title: 'Data',
      items: [
        {
          icon: TrashIcon,
          label: 'Clear Activity Log',
          description: 'Delete all recent activity entries',
          action: clearActivityLog,
        },
      ],
    },
  ];

  return (
    <div className="page-content">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{t('common.settings')}</h1>
        <p className="text-surface-400">{t('common.manageAccount')}</p>
      </div>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card mb-6"
      >
        <div className="flex items-center gap-4">
          <div className="relative">
            {resolvedAvatarUrl ? (
              <img
                src={resolvedAvatarUrl}
                alt="Avatar"
                className="w-20 h-20 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-2xl font-bold text-white">
                {user?.first_name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setShowProfileModal(true);
              }}
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-surface-800 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
              title="Edit avatar"
            >
              <PencilSquareIcon className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">
              {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-surface-400">@{user?.username}</p>
            <p className="text-sm text-surface-500">{user?.email}</p>
          </div>
        </div>
      </motion.div>

      {/* Important Dates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between mb-3 px-1">
          <h3 className="text-sm font-medium text-surface-400">Important Dates</h3>
          <button
            onClick={() => setShowAddDateModal(true)}
            className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="card">
          {loadingDates ? (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : specialDates.length === 0 ? (
            <div className="text-center py-6">
              <CakeIcon className="w-10 h-10 mx-auto mb-2 text-surface-500" />
              <p className="text-surface-400 text-sm">No important dates yet</p>
              <button
                onClick={() => setShowAddDateModal(true)}
                className="mt-2 text-sm text-primary-400 hover:text-primary-300"
              >
                Add your birthday, name day, or anniversary
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {specialDates.map((sd) => {
                const typeInfo = dateTypeLabels[sd.date_type] || dateTypeLabels.other;
                const IconComponent = typeInfo.icon;
                return (
                  <div key={sd.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className={`w-9 h-9 rounded-lg bg-surface-800 flex items-center justify-center ${typeInfo.color}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {sd.name || typeInfo.label}
                      </p>
                      <p className="text-xs text-surface-400">{formatDate(sd.date)}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSpecialDate(sd.id)}
                      className="p-1.5 rounded text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Settings Groups */}
      <div className="space-y-6">
        {settingsGroups.map((group, groupIndex) => (
          <motion.div
            key={group.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: groupIndex * 0.1 }}
          >
            <h3 className="text-sm font-medium text-surface-400 mb-3 px-1">
              {group.title}
            </h3>
            <div className="card divide-y divide-white/5">
              {group.items.map((item) => {
                const itemWithExtras = item as typeof item & {
                  disabled?: boolean;
                  custom?: React.ReactNode;
                  toggle?: boolean;
                  enabled?: boolean;
                };
                return (
                  <button
                    key={item.label}
                    onClick={item.action}
                    disabled={itemWithExtras.disabled}
                    className={`w-full flex items-center gap-4 py-4 first:pt-0 last:pb-0 text-left transition-opacity ${
                      itemWithExtras.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-surface-800 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-surface-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white">{item.label}</p>
                      <p className="text-sm text-surface-400">{item.description}</p>
                    </div>
                    {itemWithExtras.custom ? (
                      itemWithExtras.custom
                    ) : itemWithExtras.toggle ? (
                      <div
                        className={`w-11 h-6 rounded-full transition-colors ${
                          itemWithExtras.enabled ? 'bg-primary-500' : 'bg-surface-600'
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
                            itemWithExtras.enabled ? 'translate-x-5' : 'translate-x-0.5'
                          } mt-0.5`}
                        />
                      </div>
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-surface-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Logout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8"
      >
        <button
          onClick={handleLogout}
          className="w-full card flex items-center gap-4 text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
          </div>
          <span className="font-medium">{t('common.logout')}</span>
        </button>
      </motion.div>

      {/* App Info */}
      <div className="mt-8 text-center text-sm text-surface-500">
        <p>{t('common.appInfo')}</p>
        <p className="mt-1">{t('common.madeWithLove')}</p>
        {/* Hidden Debug Button - barely visible at bottom */}
        <button
          onClick={handleOpenDebugModal}
          className="mt-4 mx-auto w-6 h-6 rounded-full bg-surface-800/30 border border-white/5 flex items-center justify-center hover:bg-surface-800/50 transition-colors opacity-20 hover:opacity-40"
          title="Debug Notifications"
        >
          <BugAntIcon className="w-3 h-3 text-surface-400" />
        </button>
      </div>

      {/* Debug Modal */}
      <AnimatePresence>
        {showDebugModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowDebugModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Push Notification Debug</h2>
                  <p className="text-sm text-surface-400 mt-1">Manage registered devices and subscriptions</p>
                </div>
                <button onClick={() => setShowDebugModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Current Device Info */}
              <div className="card mb-4 p-4">
                <h3 className="text-sm font-semibold text-white mb-2">Current Device</h3>
                <div className="text-sm text-surface-400 space-y-1">
                  <p>User Agent: <span className="text-surface-300">{navigator.userAgent}</span></p>
                  <p>Permission: <span className="text-surface-300">{Notification.permission}</span></p>
                  <p>Service Worker: <span className="text-surface-300">{'serviceWorker' in navigator ? 'Supported' : 'Not Supported'}</span></p>
                  <p>Push Manager: <span className="text-surface-300">{'PushManager' in window ? 'Supported' : 'Not Supported'}</span></p>
                  <p>VAPID Key: <span className="text-surface-300 text-xs break-all">{import.meta.env.VITE_VAPID_PUBLIC_KEY ? `${import.meta.env.VITE_VAPID_PUBLIC_KEY.substring(0, 20)}...` : 'Not configured'}</span></p>
                </div>
                <button
                  onClick={handleReregister}
                  disabled={reregistering}
                  className="btn-primary mt-3 w-full"
                >
                  {reregistering ? 'Re-registering...' : 'Re-register This Device'}
                </button>
              </div>

              {/* Registered Devices */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Registered Devices ({devices.length})</h3>
                  <button
                    onClick={loadDevices}
                    disabled={loadingDevices}
                    className="btn-secondary text-sm"
                  >
                    {loadingDevices ? 'Loading...' : 'Refresh'}
                  </button>
                </div>

                {loadingDevices ? (
                  <div className="text-center py-8 text-surface-400">Loading devices...</div>
                ) : devices.length === 0 ? (
                  <div className="text-center py-8 text-surface-400">No devices registered</div>
                ) : (
                  <div className="space-y-3">
                    {devices.map((device) => {
                      const isCurrentDevice = currentEndpoint && device.endpoint === currentEndpoint;
                      return (
                        <div
                          key={device.id}
                          className={`p-4 bg-surface-800 rounded-lg border ${
                            isCurrentDevice ? 'border-primary-500/50' : 'border-white/5'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="font-medium text-white">{device.device_name || 'Unknown Device'}</p>
                                {isCurrentDevice && (
                                  <span className="px-2 py-0.5 rounded text-xs bg-primary-500/20 text-primary-400">
                                    Current Device
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-0.5 rounded text-xs ${
                                    device.is_active
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {device.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              <div className="text-xs text-surface-400 space-y-1">
                                <p>ID: <span className="text-surface-300 font-mono">{device.id}</span></p>
                                <p>Endpoint: <span className="text-surface-300 font-mono text-[10px] break-all">{device.endpoint || 'N/A'}</span></p>
                                <p>Created: <span className="text-surface-300">{new Date(device.created_at).toLocaleString()}</span></p>
                                {device.last_used_at && (
                                  <p>Last Used: <span className="text-surface-300">{new Date(device.last_used_at).toLocaleString()}</span></p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnsubscribe(device.id)}
                              className="btn-secondary text-sm ml-4"
                            >
                              Unsubscribe
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowProfileModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {resolvedAvatarUrl ? (
                      <img
                        src={resolvedAvatarUrl}
                        alt="Avatar"
                        className="w-24 h-24 rounded-full object-cover border border-white/10"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-3xl font-bold text-white">
                        {user?.first_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-surface-800 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                      title="Change avatar"
                    >
                      <PencilSquareIcon className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <div className="text-sm text-surface-400">
                    <p className="text-white font-semibold">{t('settings.editProfile')}</p>
                    <p>Upload and crop your avatar. Square images work best.</p>
                  </div>
                </div>
                <button onClick={() => setShowProfileModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitProfile(onSaveProfile)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">{t('register.firstName')}</label>
                  <input className="input" {...registerProfile('first_name', { required: true })} />
                </div>

                <div className="input-group">
                  <label className="input-label">{t('register.lastName')}</label>
                  <input className="input" {...registerProfile('last_name')} />
                </div>

                <div className="input-group">
                  <label className="input-label">{t('register.email')}</label>
                  <input type="email" className="input" {...registerProfile('email')} />
                </div>

                <div className="input-group">
                  <label className="input-label">{t('register.firstName')}</label>
                  <input type="date" className="input" {...registerProfile('date_of_birth')} />
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setAvatarPreview(reader.result as string);
                      setShowAvatarCropper(true);
                      setZoom(1);
                      setCrop({ x: 0, y: 0 });
                    };
                    reader.readAsDataURL(file);
                  }}
                />

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(false)}
                    className="btn-secondary flex-1"
                  >
                    {t('families.cancel')}
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    {t('settings.save') ?? 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Avatar Cropper */}
      <AnimatePresence initial={false}>
        {showAvatarCropper && avatarPreview && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAvatarCropper(false)}
            />
            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Crop avatar</h3>
                <button onClick={() => setShowAvatarCropper(false)} className="btn-icon">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="relative w-full aspect-square bg-surface-800 rounded-2xl overflow-hidden">
                <Cropper
                  image={avatarPreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                />
              </div>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-surface-400">Zoom</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full accent-primary-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAvatarCropper(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={avatarUploading || processingAvatar}
                    onClick={onUploadAvatar}
                    className="btn-primary flex-1"
                  >
                    {(avatarUploading || processingAvatar) ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Link Google Account Password Modal */}
      <AnimatePresence>
        {showLinkPasswordModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => {
                setShowLinkPasswordModal(false);
                setLinkPassword('');
                setPendingGoogleToken(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content max-w-md"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">Link Google Account</h2>
                  <p className="text-sm text-surface-400">
                    Enter your current password to verify your identity and link your Google account.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowLinkPasswordModal(false);
                    setLinkPassword('');
                    setPendingGoogleToken(null);
                  }}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Current Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Enter your password"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && linkPassword) {
                        handleConfirmLinkGoogle();
                      }
                    }}
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    className="btn-secondary flex-1"
                    onClick={() => {
                      setShowLinkPasswordModal(false);
                      setLinkPassword('');
                      setPendingGoogleToken(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary flex-1"
                    onClick={handleConfirmLinkGoogle}
                    disabled={!linkPassword || linkingGoogle}
                  >
                    {linkingGoogle ? (
                      <div className="flex items-center gap-2 justify-center">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Linking...</span>
                      </div>
                    ) : (
                      'Link Account'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Special Date Modal */}
      <AnimatePresence>
        {showAddDateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAddDateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Important Date</h2>
                <button onClick={() => setShowAddDateModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Type</label>
                  <select
                    className="input"
                    value={newDateType}
                    onChange={(e) => setNewDateType(e.target.value)}
                  >
                    <option value="birthday">🎂 Birthday</option>
                    <option value="nameday">✨ Name Day</option>
                    <option value="anniversary">💕 Anniversary</option>
                    <option value="wedding">💒 Wedding Anniversary</option>
                    <option value="other">📅 Other</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Date</label>
                  <input
                    type="date"
                    className="input"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Custom Name (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder={`e.g., ${newDateType === 'anniversary' ? '10th Anniversary' : 'My special day'}`}
                    value={newDateName}
                    onChange={(e) => setNewDateName(e.target.value)}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddDateModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSpecialDate}
                    className="btn-primary flex-1"
                  >
                    Add Date
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

